import React, { useState } from 'react';

const SECTIONS = [
  {
    title: 'The Racks',
    items: [
      {
        q: 'How many balls does each module hold?',
        a: 'Each module holds exactly five golf balls in a single row. The slots are concave so balls sit securely — no rattling, no rolling, no Velcro.',
      },
      {
        q: 'Can I stack multiple modules?',
        a: "Yes. Modules use a tongue-and-groove joint and stack flush, up to five high in a single rack (5×5 = 25 balls). Start with one and add more as your collection grows — no re-drilling required, the new module locks onto the one below.",
      },
      {
        q: 'What colors are available?',
        a: 'Three colors are in continuous production: Navy Blue, Crimson Red, and White. Brand-extension colors (Cream, Ink, Gold, Forest) are available on request and ship in 2–3 weeks.',
      },
      {
        q: 'Can I mix colors in one rack?',
        a: 'Yes — that\'s the whole point. On any multi-module product page, click each row in the preview to paint it independently. Common combos: a single accent row, the patriot R-W-B stack, or all-one-color for a minimalist look.',
      },
      {
        q: 'Are white rows for empty display?',
        a: "By default, yes — white rows render with empty rings on the product page to show how a 'display only' or 'leave room to grow' configuration looks. The physical rack still has slots; you can fill them whenever you want.",
      },
    ],
  },
  {
    title: 'Materials & Build',
    items: [
      {
        q: 'What are the racks made of?',
        a: "PETG filament — a rigid, weather-resistant plastic that's stronger and more impact-tolerant than standard PLA. Each rack is 3D-printed in our Phoenix, Arizona workshop and finished by hand before it ships.",
      },
      {
        q: 'How strong are they? Will they hold weight?',
        a: 'The tongue-and-groove joint is rated to 40 lbs of shear load — more than 8× the weight of a fully-loaded 5×5 rack. The mounting points are printed at solid infill so the screws bite hard.',
      },
      {
        q: 'Are they weatherproof?',
        a: "PETG is UV-resistant and won't warp in heat — fine in a covered patio, garage, or pro shop window. We don't recommend leaving racks in direct full sun for years on end, but a typical indoor or sheltered installation will outlast the wall it's on.",
      },
      {
        q: 'How big is each module?',
        a: 'Each module is roughly 9.5" wide × 3.4" tall × 1.7" deep. A 5-high stack is about 9.5" wide × 18" tall — roughly the size of a kitchen cabinet door.',
      },
    ],
  },
  {
    title: 'Installation',
    items: [
      {
        q: 'How do I mount it on the wall?',
        a: "Two drywall anchors and screws ship in the box, along with a printed level template. Tape the template to the wall, drill two holes, tap in the anchors, drive the screws, hang the rack. Two minutes start to finish.",
      },
      {
        q: 'Do I need to re-drill when I add modules?',
        a: 'No. Only the bottom module mounts to the wall — every module above just snaps onto the one below via the tongue-and-groove joint. Stack additions are a 30-second job.',
      },
      {
        q: 'Can I mount it on something other than drywall?',
        a: 'Yes. The included anchors are sized for drywall, but the screws will go straight into wood studs, plywood, or shiplap. For brick, tile, or concrete you\'ll need masonry anchors (any hardware store carries them).',
      },
      {
        q: 'Do I need extra mounting hardware for stacked modules?',
        a: "No — the tongue-and-groove joint holds the stack together. If you ever want spare drywall anchors and screws (for moving a rack or starting a second wall), the Wall Mounting Kit ($4) includes a full set plus a fresh level template.",
      },
    ],
  },
  {
    title: 'Ordering & Shipping',
    items: [
      {
        q: 'How long until my rack ships?',
        a: 'Most orders ship within 3–5 business days. Every rack is 3D-printed to order — we don\'t keep pre-made stock — but we print daily and turn around quickly.',
      },
      {
        q: 'How much is shipping?',
        a: 'Flat-rate $5.95 via USPS within the United States. Orders over $80 ship free, which usually means a Trophy Pair plus a Back-Nine, a Foursome on its own, or any Trophy Wall.',
      },
      {
        q: 'Do you ship internationally?',
        a: "Not yet. We're working on it — drop us a note via the contact page if you'd like to be on the list when we open up international shipping.",
      },
      {
        q: 'How can I track my order?',
        a: "You'll get a tracking number by email when it ships. If you placed an order without an account, use the order-lookup page with your order number and email.",
      },
    ],
  },
  {
    title: 'Returns & Guarantee',
    items: [
      {
        q: 'What if my rack arrives damaged?',
        a: "Email us a photo and we'll print and ship a replacement, free of charge — no need to return the broken one. We try to make this as painless as possible because we know freight handling can be rough.",
      },
      {
        q: 'What if I break a rack later?',
        a: "Lifetime rack guarantee. Snap one, drop one, golf-ball one — email us a photo and we'll print a replacement in the same color and ship it free. No fine print, no proof of purchase needed beyond the photo.",
      },
      {
        q: 'Can I return a rack I just don\'t like?',
        a: "Because every rack is printed to your color spec, we don't accept returns on undamaged items. If you want to swap colors, just email us within 14 days of delivery and we'll print the replacement at our cost — you only pay return shipping on the original.",
      },
    ],
  },
];

export default function FAQPage() {
  const [openKey, setOpenKey] = useState(null);

  return (
    <div className="info-page" style={{ maxWidth: 820 }}>
      <p className="mono" style={{ color: 'var(--gold)', marginBottom: '8px' }}>SUPPORT</p>
      <h1 style={{ fontFamily: 'var(--font-display)', fontWeight: 300 }}>
        Frequently asked <em style={{ color: 'var(--gold)', fontStyle: 'italic' }}>questions.</em>
      </h1>
      <p style={{ color: 'var(--cream-dim)', marginTop: 16, marginBottom: 48, fontWeight: 300 }}>
        The short version: 5-ball modules, stack up to 5 high, 3D-printed in Phoenix, ship in 3–5 days, lifetime guarantee. The longer version is below.
      </p>

      {SECTIONS.map((section, sIdx) => (
        <div key={section.title} style={{ marginBottom: 48 }}>
          <div className="mono" style={{ color: 'var(--gold-dim)', marginBottom: 18, fontSize: '0.6rem' }}>
            {String(sIdx + 1).padStart(2, '0')} — {section.title.toUpperCase()}
          </div>
          {section.items.map((faq, i) => {
            const key = `${sIdx}-${i}`;
            const open = openKey === key;
            return (
              <div key={key} style={{ borderBottom: '1px solid rgba(201,168,76,0.08)' }}>
                <button
                  onClick={() => setOpenKey(open ? null : key)}
                  style={{
                    width: '100%', textAlign: 'left', padding: '20px 0',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16,
                    fontFamily: 'var(--font-body)', fontSize: '1rem', fontWeight: 400,
                    color: 'var(--cream)', cursor: 'pointer', background: 'none', border: 'none',
                    transition: 'color 0.3s',
                  }}
                >
                  {faq.q}
                  <span style={{
                    fontFamily: 'var(--font-display)', fontSize: '1.4rem', fontWeight: 300,
                    color: 'var(--gold-dim)', transition: 'transform 0.4s',
                    transform: open ? 'rotate(45deg)' : 'none', lineHeight: 1, flexShrink: 0,
                  }}>+</span>
                </button>
                <div style={{
                  maxHeight: open ? 320 : 0, overflow: 'hidden',
                  transition: 'max-height 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94), padding 0.4s',
                  padding: open ? '0 0 22px' : 0,
                }}>
                  <p style={{ color: 'var(--cream-dim)', fontWeight: 300, lineHeight: 1.75, fontSize: '0.92rem', maxWidth: 680 }}>
                    {faq.a}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      ))}

      <div style={{ marginTop: 64, padding: '32px 0', borderTop: '1px solid rgba(201,168,76,0.12)', textAlign: 'center' }}>
        <div className="mono" style={{ color: 'var(--gold-dim)', marginBottom: 12, fontSize: '0.6rem' }}>STILL STUCK</div>
        <p style={{ color: 'var(--cream-dim)', fontWeight: 300, marginBottom: 16 }}>
          Don't see your question? Send us a note and we'll answer within a day.
        </p>
        <a href="/contact" className="btn-editorial">Contact us <span>&rarr;</span></a>
      </div>
    </div>
  );
}
